import React, { useState, useEffect, useCallback } from 'react';
import * as styles from '@/components/organisms/Header/Header.css';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Button from 'components/atoms/Button/Button';
import logoImg from '@/assets/img/logo.png';
import mypageImg from '@/assets/img/mypage.png';
import { Icon } from 'icons';
import clsx from 'clsx';
import { studentAuthApi } from '@/services/api';
import { publicAcademyApi } from '@/services/api';
import { isReservedSegment, sanitizeSlug } from '@/utils/slug';
import { useSessionSlug } from '@/hooks/useSessionSlug';



export function Header() {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [userName, setUserName] = useState('');
    const [academyName, setAcademyName] = useState('');
    const [slugAcademyName, setSlugAcademyName] = useState('');
    const [logoUrl, setLogoUrl] = useState<string | null | undefined>(undefined);
    const [isScrolled, setIsScrolled] = useState(false); // 스크롤 초기화
    const [isMenuOpen, setIsMenuOpen] = useState(false); // 햄버거 메뉴 열림 상태
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { slug: sessionSlug } = useSessionSlug();

    // 로그아웃 처리
    const handleLogout = useCallback(async () => {
        try {
            // 백엔드 로그아웃 API 호출 (연속 완료 세션 종료)
            await studentAuthApi.logout();
        } catch (error) {
            console.error('로그아웃 API 호출 실패:', error);
            // API 실패해도 로컬 로그아웃은 진행
        }
        
        // 로컬 스토리지 정리
        localStorage.removeItem('student_token');
        localStorage.removeItem('student_refresh_token');
        localStorage.removeItem('student_user_id');
        localStorage.removeItem('student_user_name');
        localStorage.removeItem('student_display_name');
        localStorage.removeItem('student_email');
        localStorage.removeItem('student_academy_name');
        localStorage.removeItem('student_grade_label');
        localStorage.removeItem('student_birth_date');
        setIsLoggedIn(false);
        setUserName('');
        setAcademyName('');
        if (pathname !== '/login' && !pathname.startsWith('/sign_up') && !pathname.startsWith('/find_')) {
            navigate('/login', { replace: true });
        }
    }, [pathname, navigate]);

    // 토큰 유효성 검사 및 리프레시 시도
    const validateToken = useCallback(async () => {
        const token = localStorage.getItem('student_token');
        const refreshToken = localStorage.getItem('student_refresh_token');
        
        if (!token) {
            setIsLoggedIn(false);
            return;
        }

        try {
            // JWT 토큰 파싱하여 만료 시간 확인
            const payload = JSON.parse(atob(token.split('.')[1]));
            const expSec = payload?.exp ? Number(payload.exp) : 0;
            const nowSec = Math.floor(Date.now() / 1000);

            // 토큰이 만료되었거나 만료 임박(60초 이내)이면 리프레시 시도
            if (expSec && expSec - nowSec <= 60) {
                if (refreshToken) {
                    try {
                        const response = await studentAuthApi.refresh({ refreshToken });
                        if (response.success && response.data?.token) {
                            localStorage.setItem('student_token', response.data.token);
                            setIsLoggedIn(true);
                        } else {
                            // 리프레시 실패 - 로그아웃 처리
                            handleLogout();
                        }
                    } catch (error) {
                        // 리프레시 실패 - 로그아웃 처리
                        console.log('Token refresh failed, logging out...');
                        handleLogout();
                    }
                } else {
                    // 리프레시 토큰 없음 - 로그아웃 처리
                    handleLogout();
                }
            } else {
                // 토큰이 아직 유효함
                setIsLoggedIn(true);
            }
        } catch (error) {
            // 토큰 파싱 실패 - 로그아웃 처리
            console.log('Token parsing failed, logging out...');
            handleLogout();
        }
    }, [handleLogout]);

    // 학생 로그인 정보 확인 (경로 변경 시마다 체크)
    useEffect(() => {
        const displayName = localStorage.getItem('student_display_name');
        const academy = localStorage.getItem('student_academy_name');
        
        setUserName(displayName || '');
        setAcademyName(academy || '');
        
        // 토큰 유효성 검사
        validateToken();
    }, [pathname, validateToken]);

    // 스크롤 여부 상태
    useEffect(() => {
        const onScroll = () => setIsScrolled(window.scrollY > 0);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);
    
    // 학원 로고 로딩 (slug 기반)
    useEffect(() => {
        setLogoUrl(undefined);
            const segments = pathname.split('/').filter(Boolean);
            const first = segments[0] || '';
        const slugCandidate = (!isReservedSegment(first) && first)
            ? sanitizeSlug(first)
            : sanitizeSlug(sessionSlug || '');
            if (!slugCandidate) {
                setLogoUrl(null);
            setSlugAcademyName('');
                return;
            }
        publicAcademyApi
            .getByUrl(slugCandidate)
            .then((res: any) => {
                    const data = res?.data || res;
                    const nextLogo = data?.logoUrl || null;
                setLogoUrl(nextLogo ? String(nextLogo) : null);
                const nextAcademyName = data?.name || data?.academyName || data?.displayName || '';
                setSlugAcademyName(nextAcademyName || '');
            })
            .catch(() => {
                setLogoUrl(null);
                setSlugAcademyName('');
            });
    }, [pathname, sessionSlug]);

    // 경로 변경 시 햄버거 메뉴 닫기
    useEffect(() => {
        setIsMenuOpen(false);
    }, [pathname]);

    // /academy 가 있으면 렌더링하지 않음
    if (pathname.includes('/academy/')) {
        return null;
    }
    // slug 프리픽스 계산
    const segments = pathname.split('/').filter(Boolean);
    const first = segments[0] || '';
    const slugValue = (!isReservedSegment(first) && first) ? first : (sessionSlug || '');
    const slugPrefix = slugValue ? `/${slugValue}` : '';
    const resolvedAcademyLabel = academyName || slugAcademyName || slugValue;

    const goLogin = () => {
        setIsMenuOpen(false);
        navigate(slugPrefix ? `${slugPrefix}/login` : '/login', { replace: true });
    };
    const goSignUp = () => {
        setIsMenuOpen(false);
        navigate(slugPrefix ? `${slugPrefix}/sign_up` : '/sign_up', { replace: true });
    };
    const goHome = () => navigate(slugPrefix || '/', { replace: false });
    const homePath = slugPrefix || '/';
    
    // 메뉴 항목 클릭 핸들러
    const handleMenuClick = (path: string) => {
        setIsMenuOpen(false);
        navigate(path);
    };
  return (
    <header 
    className={clsx(
        'flex sticky top-0 justify-center mx-auto w-full border-b border-gray-100 transition-shadow z-[101] lg:static lg:pt-6 lg:border-b-0 lg:px-5',
        isScrolled ? 'lg:!pt-0 lg:bg-white':'shadow-none'
      )}
    >
        {/* 해더 하얀부분 */}
        <div
        //  className='bg-white w-full max-w-[1280px] px-5 lg:px-10 flex flex-col items-center justify-between lg:rounded-2xl'
         className={clsx(
            'flex items-center flex-col justify-between w-full bg-white transition-all px-5 lg:px-0 !max-w-[1240px]',
            // 스크롤 안 된 상태
            !isScrolled && 'mx-auto lg:rounded-2xl',
            // 스크롤 된 상태
            isScrolled && 'rounded-none max-w-full'
          )}
        
        >
            <div className='flex items-center justify-between lg:px-6 w-full py-[6px] lg:py-6 max-w-[1280px]'>
                <Link to={isLoggedIn ? homePath : (slugPrefix ? `${slugPrefix}/login` : '/login')} className='flex items-center gap-2 min-h-[28px] lg:min-h-[40px]'>
                {logoUrl === undefined ? (
                    <div className='w-[90px] h-[28px] lg:w-[110px] lg:h-[40px]' />
                ) : (
                <img 
                    src={logoUrl || logoImg} 
                    alt="학원 로고" 
                        className='lg:max-h-[40px] max-h-[28px] object-cover' 
                    onError={(e) => {
                        const target = e.currentTarget as HTMLImageElement;
                        if (target.src !== logoImg) {
                            target.src = logoImg;
                        }
                    }}
                />
                )}
                {logoUrl !== undefined && resolvedAcademyLabel && (
                    <span className='text-sm font-semibold text-gray-800 whitespace-nowrap'>
                        {resolvedAcademyLabel}
                    </span>
                )}
                </Link>
                {/* 로그인 상태 모음 박스 */}
                <div className='hidden lg:block'>
                    {/* 로그인 전/후 UI */}
                    {isLoggedIn ? (
                    // 로그인 후
                    <div className="flex gap-4 items-center">
                        <div className="flex gap-4 items-center cursor-pointer" onClick={() => navigate(slugPrefix ? `${slugPrefix}/mypage/info` : '/mypage/info')}>
                            <img src={mypageImg} alt="아이콘" className="w-7 h-7" />
                            <div className="flex flex-col items-start">
                                <p className="text-sm">{userName || '학생'}</p>
                                <p className="text-xs text-gray-600">{academyName || slugAcademyName || '학원'}</p>
                            </div>
                        </div>
                    </div>
                    ) : (
                    // 로그인 전
                    <div className="flex gap-4 items-center">
                        <Button size="small" intent="secondary" text onClick={goLogin}>
                            로그인
                        </Button>
                    </div>
                    )}
                </div>
                <button 
                    className='flex justify-center items-center w-10 h-10 text-gray-900 bg-white lg:hidden'
                    onClick={() => setIsMenuOpen(true)}
                >
                    <Icon name="menu" size={24} style={{ marginLeft: 8 }} />    
                </button>
            </div>
            
            <nav className='lg:flex w-full gap-4 py-2 text-lg font-bold text-gray-900 hidden max-w-[1280px] lg:px-10'>
                <Link to={slugPrefix ? `${slugPrefix}/learning/list` : '/learning/list'} className={`${styles.navItem} hover:bg-blue-50 transition-all duration-200`}>학습</Link>
                <Link to={slugPrefix ? `${slugPrefix}/homework/list` : '/homework/list'} className={`${styles.navItem} hover:bg-blue-50 transition-all duration-200`}>숙제</Link>
                <Link to={slugPrefix ? `${slugPrefix}/vocabulary/list` : '/vocabulary/list'} className={`${styles.navItem} hover:bg-blue-50 transition-all duration-200`}>단어장</Link>
                <Link to={slugPrefix ? `${slugPrefix}/exam/list` : '/exam/list'} className={`${styles.navItem} hover:bg-blue-50 transition-all duration-200`}>온라인시험</Link>
                {/* <Link to="/playground" className={`${styles.navItem} hover:bg-blue-50 transition-all duration-200`}>플레이 그라운드 입니다</Link> */}
            </nav>
        </div>
        
        {/* 햄버거 메뉴 모달 */}
        <div 
            className={clsx(
                "fixed inset-0 bg-white transition-transform duration-300 ease-in-out z-[102] lg:hidden",
                isMenuOpen ? "translate-x-0" : "translate-x-full pointer-events-none"
            )}
            onClick={() => setIsMenuOpen(false)}
        >
            <div 
                className="flex flex-col w-full h-full"
                onClick={(e) => e.stopPropagation()}
            >
                    {/* 상단 헤더 (로고 + 닫기 버튼) */}
                    <div className="flex justify-between items-center px-5 pt-5">
                        <Link 
                            to={isLoggedIn ? homePath : (slugPrefix ? `${slugPrefix}/login` : '/login')} 
                            className='flex items-center gap-2 min-h-[28px]'
                            onClick={() => setIsMenuOpen(false)}
                        >
                            {logoUrl === undefined ? (
                                <div className='w-[90px] h-[28px]' />
                            ) : (
                                <img 
                                    src={logoUrl || logoImg} 
                                    alt="학원 로고" 
                                    className='max-h-[28px] object-cover' 
                                    onError={(e) => {
                                        const target = e.currentTarget as HTMLImageElement;
                                        if (target.src !== logoImg) {
                                            target.src = logoImg;
                                        }
                                    }}
                                />
                            )}
                            {logoUrl !== undefined && resolvedAcademyLabel && (
                                <span className='text-sm font-semibold text-gray-800 whitespace-nowrap'>
                                    {resolvedAcademyLabel}
                                </span>
                            )}
                        </Link>
                        <button 
                            className='flex justify-end items-center w-10 h-10 text-gray-900'
                            onClick={() => setIsMenuOpen(false)}
                        >
                            <Icon name="close" size={20} className='*:fill-gray-900' />
                        </button>
                    </div>
                    
                    {/* 로그인 전/후 버튼 영역 */}
                    <div className="flex justify-between items-center px-5 py-5 border-b border-gray-100">
                        {!isLoggedIn ? (
                            <Button 
                                size="small" 
                                intent="secondary" 
                                text 
                                onClick={goLogin}
                            >
                                로그인
                            </Button>
                        ) : (
                            <button
                                onClick={() => {
                                    setIsMenuOpen(false);
                                    navigate(slugPrefix ? `${slugPrefix}/mypage/info` : '/mypage/info');
                                }}
                                className="flex flex-1 gap-2 items-center"
                            >
                                <div className="flex overflow-hidden justify-center items-center w-8 h-8 bg-gray-300 rounded-full">
                                    <img src={mypageImg} alt="아이콘" className="w-7 h-7" />
                                </div>
                                <div className="flex flex-col flex-1 items-start">
                                    <p className="text-sm text-gray-900">{userName || '학생'}</p>
                                    <p className="text-xs text-gray-500">{academyName || slugAcademyName || '학원'}</p>
                                </div>
                                <Icon 
                                    name="arrowDown" 
                                    size={24} 
                                    style={{ transform: 'rotate(-90deg)' }}
                                    className="*:fill-gray-300"
                                />
                            </button>
                        )}
                    </div>
                    
                    {/* 메뉴 항목들 */}
                    <div className="flex flex-col flex-1 gap-1 px-5 py-5">
                        <button
                            onClick={() => handleMenuClick(slugPrefix ? `${slugPrefix}/learning/list` : '/learning/list')}
                            className="flex items-center justify-between gap-1 px-3 py-[10px] bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            <span className="text-lg font-bold text-gray-900">교재</span>
                            <Icon 
                                name="arrowDown" 
                                size={24} 
                                style={{ transform: 'rotate(-90deg)' }}
                            />
                        </button>
                        <button
                            onClick={() => handleMenuClick(slugPrefix ? `${slugPrefix}/homework/list` : '/homework/list')}
                            className="flex items-center justify-between gap-1 px-3 py-[10px] bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            <span className="text-lg font-bold text-gray-900">숙제</span>
                            <Icon 
                                name="arrowDown" 
                                size={24} 
                                style={{ transform: 'rotate(-90deg)' }}
                            />
                        </button>
                        <button
                            onClick={() => handleMenuClick(slugPrefix ? `${slugPrefix}/exam/list` : '/exam/list')}
                            className="flex items-center justify-between gap-1 px-3 py-[10px] bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            <span className="text-lg font-bold text-gray-900">시험</span>
                            <Icon 
                                name="arrowDown" 
                                size={24} 
                                style={{ transform: 'rotate(-90deg)' }}
                            />
                        </button>
                        <button
                            onClick={() => handleMenuClick(slugPrefix ? `${slugPrefix}/vocabulary/list` : '/vocabulary/list')}
                            className="flex items-center justify-between gap-1 px-3 py-[10px] bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            <span className="text-lg font-bold text-gray-900">단어장</span>
                            <Icon 
                                name="arrowDown" 
                                size={24} 
                                style={{ transform: 'rotate(-90deg)' }}
                            />
                        </button>
                    </div>
                </div>
        </div>
    </header>
  );
}